import { ConfigProvider, message } from 'antd';
import enGB from 'antd/lib/locale/en_GB';
import countries from 'i18n-iso-countries';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { IntlProvider } from 'react-intl';
import memberApi from './api/MemberApi';
import './App.less';
import LoadingComponent from './components/LoadingComponent/LoadingComponent';
import RoutesComponent from './components/RoutesComponent/RoutesComponent';
import configuration from './configs/IConfiguration';
import CustomContext from './context/CustomContext';
import messagesEn from './i18n/en.json';
import { Context } from './model/elements';
import { CustomAuth, Member } from './model/entities';
import { deepLinkAllowedPaths, ProviderAuthType } from './model/types';
import authService from './security/IAuthService';
import comparatorService from './services/ComparatorService';
import mioAdminSettingService from './services/MioAdminSettingService';
import providerAuthService from './services/ProviderAuthService';
import stringService from './services/StringService';

/**
 * Returns the app.
 * @returns the app.
 */
const App: React.FC = () => {
    /*** HOOKS ***/
    const isCheckingProvidersRef = useRef(false);
    const [auth, setAuth] = useState<CustomAuth>();
    const [providerAuths, setProviderAuths] = useState<ProviderAuthType[]>([]);
    const [initialized, setInitialized] = useState<boolean>(false);

    // create context
    const context: Context = {
        auth,
        updateAuth: setAuth,
        providerAuths,
        updateProviderAuths: setProviderAuths,
    };

    // initialize app
    useEffect(() => {
        const init = async () => {
            try {
                // set-up antd configuration
                message.config({
                    maxCount: 1,
                });

                // set-up locale
                moment.locale('en');
                countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

                // store organization
                storeOrganization();

                // set-up current auth
                let auth = await authService.getAuth();

                // if organization is not selected, select first organization
                if (auth && !auth.organization && auth.organizations.length > 0) {
                    const organization = auth.organizations.sort((a, b) => stringService.sort(a.name, b.name))[0];
                    auth = await authService.getAuthByOrganization(organization);
                }

                if (auth && auth.organization && auth.organization.id) {
                    await mioAdminSettingService.init(auth.organization.id);

                    const providerAuths: ProviderAuthType[] = await providerAuthService.init();
                    setProviderAuths(providerAuths);

                    if (providerAuths.includes('AUTODESK')) {
                        auth.members = await loadCurrentMembers();
                    }
                }

                setAuth(auth);

                // store deep link
                storeDeeplink(auth);

                setInitialized(true);
            } catch (error) {
                console.log(error);
                message.error('Internal error');
            }
        };
        init();
    }, []);

    // set up provider auths listener
    useEffect(() => {
        const checkProviders = async () => {
            try {
                // check if checking providers is already in progress
                if (isCheckingProvidersRef.current) {
                    return;
                } else {
                    isCheckingProvidersRef.current = true;
                }

                // check providers status
                if (auth) {
                    const updatedProviderAuths: ProviderAuthType[] = await providerAuthService.refresh();
                    if (!comparatorService.areArraysEqualUnordered(providerAuths, updatedProviderAuths)) {
                        setProviderAuths(updatedProviderAuths);

                        if (updatedProviderAuths.includes('AUTODESK') && !providerAuths.includes('AUTODESK')) {
                            const members = await memberApi.getCurrentMembers();
                            setAuth({
                                ...auth,
                                organization: auth.organization,
                                organizations: auth.organizations,
                                authorities: auth.authorities,
                                members: members,
                            });
                        }
                    }
                }
            } catch (error) {
                console.log(error);
                message.error('Internal error');
            } finally {
                isCheckingProvidersRef.current = false;
            }
        };

        // set interval for checking providers periodically
        const interval = setInterval(() => {
            checkProviders();
        }, 1000);

        // stop checking providers periodically on new useEffect
        return () => {
            clearInterval(interval);
        };
    }, [providerAuths, auth, setAuth]);

    /*** METHODS ***/

    const storeDeeplink = (auth?: CustomAuth) => {
        if (
            !auth &&
            window.location.pathname &&
            deepLinkAllowedPaths.some((p) => window.location.pathname.startsWith(`/${p}`))
        ) {
            sessionStorage.setItem('deeplink', window.location.pathname + window.location.search);
        }
    };

    const storeOrganization = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            const organizationId = params.get('organizationId') || undefined;
            if (organizationId) {
                localStorage.setItem('organizationId', organizationId);
            }
        } catch (error) {
            // the first organization will be selected
        }
    };

    const loadCurrentMembers = async (): Promise<Member[]> => {
        let members: Member[] = [];
        try {
            members = await memberApi.getCurrentMembers();
        } catch (error) {
            // access token has expired, a new one will be generated
        }

        return members;
    };

    /*** VISUAL ***/

    return (
        <div className={configuration.getTenant() === 'cellnex' ? 'cellnex' : undefined} style={{ height: '100%' }}>
            <ConfigProvider locale={enGB}>
                <CustomContext.Provider value={context}>
                    <IntlProvider locale="en-GB" defaultLocale="en-GB" messages={messagesEn}>
                        {initialized ? <RoutesComponent /> : <LoadingComponent />}
                    </IntlProvider>
                </CustomContext.Provider>
            </ConfigProvider>
        </div>
    );
};
export default App;
